"use client";

import { useState } from "react";
import { ChevronDown, Heart, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const categories = ["All", "Surgical", "Diagnostic", "Aesthetics", "Dental", "AI & Digital"];
const products = [{
  id: 1,
  title: "Advanced Surgical Imaging",
  category: "Surgical",
  price: "Global",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/surgical_imaging_system.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/surgical_imaging_system.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/surgical_imaging_system.png-wvc-sizes",
  feature1: "High-definition clarity",
  feature2: "Real-time data integration",
  feature3: "Ergonomic design"
}, {
  id: 2,
  title: "Precision Diagnostic Suite",
  category: "Diagnostic",
  price: "Global",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/diagnostic_suite_pro.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/diagnostic_suite_pro.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/diagnostic_suite_pro.webp-wvc-sizes",
  feature1: "Rapid analysis tools",
  feature2: "Clinical accuracy",
  feature3: "Seamless connectivity"
}, {
  id: 3,
  title: "Aesthetic Laser Platform",
  category: "Aesthetics",
  price: "Global",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/aesthetic_laser_tech.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/aesthetic_laser_tech.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/aesthetic_laser_tech.png-wvc-sizes",
  feature1: "Advanced skin rejuvenation",
  feature2: "Safety-first protocols",
  feature3: "Customizable treatment"
}, {
  id: 4,
  title: "Digital Dental Imaging",
  category: "Dental",
  price: "Global",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/dental_imaging_solution.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/dental_imaging_solution.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/dental_imaging_solution.png-wvc-sizes",
  feature1: "Low-dose radiation",
  feature2: "3D reconstruction",
  feature3: "Workflow optimization"
}, {
  id: 5,
  title: "AI-Powered Diagnostics",
  category: "AI & Digital",
  price: "Global",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/ai_diagnostic_platform.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/ai_diagnostic_platform.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/ai_diagnostic_platform.png-wvc-sizes",
  feature1: "Predictive analytics",
  feature2: "Automated reporting",
  feature3: "Cloud-based insights"
}, {
  id: 6,
  title: "General Medical Monitor",
  category: "Diagnostic",
  price: "Global",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/general_medical_monitor.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/general_medical_monitor.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/general_medical_monitor.png-wvc-sizes",
  feature1: "Multi-parameter tracking",
  feature2: "Intuitive interface",
  feature3: "Reliable performance"
}];
export default function DiagnosticEquipmentGrid() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [sortBy, setSortBy] = useState("featured");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const filtered = activeCategory === "All" ? products : products.filter(p => p.category === activeCategory);
  return <section data-section-id="3719" id="diagnosticequipmentgrid" className="bg-background text-foreground pt-[200px] pb-24 px-6 md:px-12 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs uppercase tracking-[0.08em] text-blue-600 mb-8">
            Our Portfolio — Medical Technology
          </p>
          <h1 className="font-default font-bold text-5xl md:text-7xl lg:text-8xl leading-[1.1] tracking-tighter text-foreground max-w-5xl">
            Innovative medical
            <br />
            <span className="italic text-teal-600">
              solutions for global care.
            </span>
          </h1>
          <p className="font-default text-base md:text-lg text-gray-600 max-w-2xl mt-8 leading-relaxed">
            We source and distribute premium medical devices from leading global manufacturers. Our mission is to ensure healthcare professionals have access to the most reliable and clinically valuable technologies.
          </p>
        </div>

        <div className="mt-20 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 border-y border-gray-200 py-6">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat, i) => {
            const isActive = activeCategory === cat;
            return <Button key={cat} data-index={i} onClick={() => setActiveCategory(cat)} variant="ghost" size="sm" className={`font-default text-xs uppercase tracking-[0.1em] rounded-lg border transition-all ${isActive ? "bg-blue-900 text-white border-blue-900 hover:bg-blue-800" : "bg-transparent text-gray-900 border-gray-200 hover:border-teal-600 hover:text-teal-600"}`}>
                  {cat}
                </Button>;
          })}
          </div>

          <div className="flex items-center gap-3">
            <span className="font-default text-xs uppercase tracking-[0.1em] text-gray-500">
              Sort
            </span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] font-default text-xs uppercase tracking-[0.1em] border-gray-200 bg-transparent rounded-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="font-default text-xs">
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="availability">Availability</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {filtered.map((product, i) => <article key={product.id} data-index={i} className="group relative flex flex-col border border-gray-200 bg-white rounded-lg overflow-hidden transition-all duration-500 hover:border-teal-600/40 hover:shadow-2xl hover:scale-[1.01]">
              <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                <img src={product.image} data-wvc-srcset={product.srcset} data-wvc-sizes={product.sizes} alt={product.title} loading="lazy" decoding="async" className="w-full h-full object-cover rounded-lg transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                <Button variant="ghost" size="icon" aria-label="Save product" className="absolute top-3 right-3 size-9 rounded-lg bg-white/80 backdrop-blur-sm border border-gray-200 text-gray-900 hover:bg-teal-600 hover:text-white hover:border-teal-600">
                  <Heart className="size-4" />
                </Button>
              </div>

              <div className="flex flex-col flex-1 p-8">
                <div className="flex items-center justify-between">
                  <span className="font-default text-[10px] uppercase tracking-[0.08em] text-teal-600 font-semibold">
                    {product.category}
                  </span>
                  <span className="font-default font-bold text-xl text-blue-900">
                    {product.price}
                  </span>
                </div>

                <h3 className="font-default font-bold text-2xl leading-snug mt-4 text-gray-900">
                  {product.title}
                </h3>

                <ul className="mt-5 space-y-3">
                  {[product.feature1, product.feature2, product.feature3].map((feat, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2 font-default text-sm text-gray-700 font-medium">
                        <span className="mt-[6px] size-1 rounded-lg bg-teal-600 shrink-0" />
                        <span className="uppercase tracking-wider text-xs">{feat}</span>
                      </li>)}
                </ul>

                <Button variant="ghost" className="mt-7 w-full font-default text-xs uppercase tracking-[0.08em] border border-gray-200 bg-transparent text-gray-900 hover:bg-blue-900 hover:text-white hover:border-blue-900 rounded-lg h-11">
                  <ShoppingCart className="size-4 mr-2" />
                  Inquire Now
                </Button>
              </div>
            </article>)}
        </div>

        <div className="mt-20 pt-10 border-t border-gray-200">
          <Pagination>
            <PaginationContent className="gap-2">
              <PaginationItem>
                <PaginationPrevious href="#" className="font-default text-xs uppercase tracking-[0.15em] text-gray-900 hover:text-teal-600 hover:bg-transparent" />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" isActive className="font-default text-xs bg-teal-600 text-white border-teal-600 hover:bg-teal-700 rounded-lg">
                  01
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" className="font-default text-xs text-gray-900 hover:text-teal-600 hover:bg-transparent rounded-lg">
                  02
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" className="font-default text-xs text-gray-900 hover:text-teal-600 hover:bg-transparent rounded-lg">
                  03
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationEllipsis className="text-gray-400" />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" className="font-default text-xs text-gray-900 hover:text-teal-600 hover:bg-transparent rounded-lg">
                  08
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext href="#" className="font-default text-xs uppercase tracking-[0.15em] text-gray-900 hover:text-teal-600 hover:bg-transparent" />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    </section>;
}